import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Drawer,
    Snackbar,
    Alert,
    Chip,
} from '@mui/material';
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaTags,
    FaArrowUp,
    FaArrowDown,
} from 'react-icons/fa';
import { categoriesService } from '../../services/categoriesService';
import Loader from '../Common/Loader';
import MenuItemCategoryEditPanel from './MenuItemCategoryEditPanel';
import { useFirebase } from '../Auth/FirebaseProvider';

function MenuItemCategoryEditor() {
    const { foodTruckId, loading: contextLoading } = useFirebase();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [categoryService, setCategoryService] = useState(null);

    useEffect(() => {
        if (!foodTruckId || contextLoading) return;
        const service = categoriesService.getInstance(foodTruckId);
        setCategoryService(service);
        loadCategories(service);
    }, [foodTruckId, contextLoading]);

    const loadCategories = async (service) => {
        try {
            setLoading(true);
            const data = await service.getCategories();
            const sortedCategories = data.sort((a, b) => (a.order || 0) - (b.order || 0));
            setCategories(sortedCategories);
        } catch (error) {
            console.error("error", error);
            setError("Erreur lors du chargement des catégories");
            setSnackbar({ open: true, message: "Erreur lors du chargement des catégories", severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleMoveCategory = async (categoryId, direction) => {
        if (!categoryService) return;

        const currentIndex = categories.findIndex(cat => cat.id === categoryId);
        if ((direction === 'up' && currentIndex === 0) ||
            (direction === 'down' && currentIndex === categories.length - 1)) {
            return;
        }

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        const newCategories = [...categories];
        const currentOrder = newCategories[currentIndex].order || currentIndex;
        const targetOrder = newCategories[newIndex].order || newIndex;

        newCategories[currentIndex] = { ...newCategories[currentIndex], order: targetOrder };
        newCategories[newIndex] = { ...newCategories[newIndex], order: currentOrder };

        newCategories.sort((a, b) => (a.order || 0) - (b.order || 0));

        try {
            await categoryService.updateCategories(newCategories);
            setCategories(newCategories);
        } catch (error) {
            console.error("Error reordering categories:", error);
            setSnackbar({
                open: true,
                message: "Erreur lors du réordonnancement",
                severity: 'error'
            });
        }
    };

    const handleAddCategory = () => {
        if (!categoryService) return;
        const maxOrder = Math.max(...categories.map(cat => cat.order || 0), -1);
        setSelectedCategory({
            name: '',
            sizes: [],
            order: maxOrder + 1,
            isHidden: false
        });
        setIsEditing(true);
    };

    const handleEditCategory = (category) => {
        setSelectedCategory(category);
        setIsEditing(true);
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!categoryService || !window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
            return;
        }

        try {
            await categoryService.deleteCategory(categoryId);
            await loadCategories(categoryService);
            setSnackbar({
                open: true,
                message: "Catégorie supprimée avec succès",
                severity: 'success'
            });
        } catch (error) {
            console.error("Error deleting category:", error);
            setSnackbar({
                open: true,
                message: "Erreur lors de la suppression de la catégorie",
                severity: 'error'
            });
        }
    };

    const handleSaveCategory = async (categoryData) => {
        if (!categoryService) return;

        try {
            await categoryService.updateCategories([{
                ...categoryData,
                id: categoryData.id
            }]);
            await loadCategories(categoryService);
            setIsEditing(false);
            setSnackbar({
                open: true,
                message: categoryData.id ? "Catégorie mise à jour" : "Catégorie ajoutée",
                severity: 'success'
            });
        } catch (error) {
            console.error("Error saving category:", error);
            setSnackbar({
                open: true,
                message: "Erreur lors de la sauvegarde de la catégorie",
                severity: 'error'
            });
        }
    };

    if (contextLoading || loading) return <Loader />;
    if (!foodTruckId) return <Typography color="error">Aucun restaurant sélectionné</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Box sx={{ p: 0 }}>
            {/* Header de la page */}
            <Box sx={{ 
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                p: 3,
                mb: 3
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography 
                            variant="h4" 
                            component="h1" 
                            sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <FaTags size={20} color="white" />
                            </Box>
                            Gestion des catégories
                        </Typography>
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: 'text.secondary',
                                mt: 0.5
                            }}
                        >
                            Organisez et gérez les catégories de votre menu
                        </Typography>
                    </Box>
                    
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<FaPlus />}
                        onClick={handleAddCategory}
                        sx={{ borderRadius: 2 }}
                    >
                        Ajouter une catégorie
                    </Button>
                </Box>
            </Box>

            {/* Contenu principal */}
            <Box sx={{ px: 3 }}>
                <Paper
                    elevation={0}
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}
                >
                    <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Ordre</TableCell>
                            <TableCell>Nom</TableCell>
                            <TableCell>Points</TableCell>
                            <TableCell>Tailles disponibles</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map((category, index) => (
                            <TableRow key={category.id}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveCategory(category.id, 'up')}
                                            disabled={index === 0}
                                            sx={{
                                                bgcolor: index === 0 ? 'transparent' : 'action.hover',
                                                '&:hover': index === 0 ? {} : { bgcolor: 'primary.light' }
                                            }}
                                        >
                                            <FaArrowUp size={12} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleMoveCategory(category.id, 'down')}
                                            disabled={index === categories.length - 1}
                                            sx={{
                                                bgcolor: index === categories.length - 1 ? 'transparent' : 'action.hover',
                                                '&:hover': index === categories.length - 1 ? {} : { bgcolor: 'primary.light' }
                                            }}
                                        >
                                            <FaArrowDown size={12} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={category.name}
                                        variant="outlined"
                                        color="primary"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={`${category.complexityPoints || 0} pts`}
                                        color={category.complexityPoints > 0 ? "primary" : "default"}
                                        variant={category.complexityPoints > 0 ? "filled" : "outlined"}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label="Classique"
                                            size="small"
                                            sx={{ backgroundColor: 'action.hover', fontWeight: 'bold' }}
                                        />
                                        {category.sizes?.map((size) => (
                                            <Chip
                                                key={size.id}
                                                label={`${size.name} (+${size.extraPrice.toFixed(2)}€)`}
                                                variant="outlined"
                                                size="small"
                                                color="primary"
                                            />
                                        ))}
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                        <IconButton
                                            onClick={() => handleEditCategory(category)}
                                            size="small"
                                            sx={{
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                '&:hover': { 
                                                    bgcolor: 'primary.dark',
                                                    transform: 'scale(1.05)'
                                                }
                                            }}
                                        >
                                            <FaEdit size={14} />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDeleteCategory(category.id)}
                                            size="small"
                                            sx={{
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                '&:hover': { 
                                                    bgcolor: 'error.dark',
                                                    transform: 'scale(1.05)'
                                                }
                                            }}
                                        >
                                            <FaTrash size={14} />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    </Table>
                    </TableContainer>
                </Paper>
            </Box>

            <Drawer
                anchor="right"
                open={isEditing}
                onClose={() => setIsEditing(false)}
            >
                <MenuItemCategoryEditPanel
                    category={selectedCategory}
                    onSave={handleSaveCategory}
                    onClose={() => setIsEditing(false)}
                />
            </Drawer>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MenuItemCategoryEditor;